/*
 * PaginationBar (Figma 119:44984): top 1px --border-default, padding 24px 32px.
 * Group gap 4: PaginationButton (white, 1px --border-strong inside stroke that takes no layout space, height 36, padding 4px 12px, radius 6,
 * 550 14/20 tracking 0.14px) · PageNumber (36×36, padding 4px, radius 6, Medium 500 14/20, centered;
 * current --interaction-active; hover --interaction-hover; keyboard focus "Focus M") · "…".
 * Right: "Näitan ridu:" 400 --text-weaker + "15 / 63" 550 --text-default, gap 8, tracking 0.14px.
 * Not in the design: density "compact" (padding 16px 32px) for pages that need the height.
 * Narrow screens (not in Figma): the sides are the page gutter (--layout-page-padding-x); on a
 * phone (below 640) Previous / Next are arrow buttons (their labels as accessible names), only
 * the first, the current and the last page show, and "rows shown" wraps under them. On a touch
 * screen every button is --size-touch-target (40px) high.
 */
import { cx } from './cx'
import { Icon } from './Icon'
import { caretDown16 } from './icons'
import { PHONE, useMediaQuery } from './useMediaQuery'

/**
 * Page numbers to show: first, last, and `size` pages around the current one (clamped, so with
 * the default 3, page 1 shows 1 2 3 … N as in the design), with null for an ellipsis.
 */
export function pageWindow(current: number, total: number, size = 3): Array<number | null> {
    if (total <= size + 4) return Array.from({ length: total }, (_, i) => i + 1)
    const start = Math.min(Math.max(current - Math.floor((size - 1) / 2), 1), total - size + 1)
    const pages = new Set([1, total, ...Array.from({ length: size }, (_, i) => start + i)])
    const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)
    const out: Array<number | null> = []
    sorted.forEach((page, i) => {
        if (i > 0 && page - sorted[i - 1]! > 1) out.push(null)
        out.push(page)
    })
    return out
}

const NAV_BUTTON =
    'flex h-[36px] shrink-0 cursor-pointer items-center justify-center gap-[6px] rounded-[6px] bg-[var(--surface-default)] py-[4px] shadow-[inset_0_0_0_1px_var(--border-strong)] ' +
    'whitespace-nowrap text-[14px] leading-[20px] font-[550] tracking-[0.14px] text-[var(--text-default)] ' +
    'focus-visible:shadow-[inset_0_0_0_1px_var(--border-strong),var(--focus-m)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ' +
    'pointer-coarse:h-[var(--size-touch-target)]'

export function Pagination({
    page,
    pageCount,
    onPage,
    shownLabel,
    shown,
    total,
    previousLabel,
    nextLabel,
    pageLabel,
    density = 'default',
}: {
    page: number
    pageCount: number
    onPage: (page: number) => void
    shownLabel: string
    shown: number
    total: number
    previousLabel: string
    nextLabel: string
    pageLabel: (page: number) => string
    /** "compact": 16px instead of 24px above and below. */
    density?: 'default' | 'compact'
}) {
    const phone = useMediaQuery(PHONE)
    return (
        <nav
            aria-label={shownLabel}
            className={cx(
                'flex w-full flex-wrap items-center gap-y-[12px] border-t border-solid border-[var(--border-default)] px-[var(--layout-page-padding-x)]',
                density === 'compact' ? 'py-[16px]' : 'py-[24px] max-sm:py-[16px]',
            )}
        >
            <div className="flex items-center gap-[4px]">
                <button
                    type="button"
                    className={cx(NAV_BUTTON, phone ? 'w-[36px] pointer-coarse:w-[var(--size-touch-target)]' : 'px-[12px]')}
                    aria-label={phone ? previousLabel : undefined}
                    disabled={page <= 1}
                    onClick={() => onPage(page - 1)}
                >
                    {phone ? <Icon src={caretDown16} size={16} className="rotate-90" /> : previousLabel}
                </button>
                {pageWindow(page, pageCount, phone ? 1 : 3).map((p, i) =>
                    p === null ? (
                        <span
                            key={`gap-${i}`}
                            aria-hidden
                            className={cx(
                                'flex h-[36px] items-center justify-center py-[4px] text-center text-[14px] leading-[20px] font-medium text-[var(--text-default)]',
                                phone ? 'w-[20px]' : 'w-[36px] px-[4px]',
                            )}
                        >
                            ...
                        </span>
                    ) : (
                        <button
                            key={p}
                            type="button"
                            aria-label={pageLabel(p)}
                            aria-current={p === page ? 'page' : undefined}
                            onClick={() => onPage(p)}
                            className={cx(
                                'flex size-[36px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] px-[4px] py-[4px]',
                                'text-center text-[14px] leading-[20px] font-medium whitespace-nowrap text-[var(--text-default)]',
                                'focus-visible:bg-[var(--surface-raised)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none',
                                'pointer-coarse:size-[var(--size-touch-target)]',
                                p === page ? 'bg-[var(--interaction-active)]' : 'hover:bg-[var(--interaction-hover)]',
                            )}
                        >
                            {p}
                        </button>
                    ),
                )}
                <button
                    type="button"
                    className={cx(NAV_BUTTON, phone ? 'w-[36px] pointer-coarse:w-[var(--size-touch-target)]' : 'px-[12px]')}
                    aria-label={phone ? nextLabel : undefined}
                    disabled={page >= pageCount}
                    onClick={() => onPage(page + 1)}
                >
                    {phone ? <Icon src={caretDown16} size={16} className="-rotate-90" /> : nextLabel}
                </button>
            </div>
            <div className="h-px min-w-px flex-1" />
            <p className="m-0 flex items-center gap-[8px] overflow-clip whitespace-nowrap text-[14px] leading-[20px] tracking-[0.14px]">
                <span className="font-normal text-[var(--text-weaker)]">{shownLabel}</span>
                <span className="font-[550] text-[var(--text-default)]">
                    {shown} / {total}
                </span>
            </p>
        </nav>
    )
}
